import { useState } from 'react';

interface Area {
  icon: string;
  titulo: string;
  itens: string[];
}

const areas: Area[] = [
  {
    icon: 'ri-scales-3-line',
    titulo: 'Civil',
    itens: [
      'Responsabilidade Civil',
      'Contratos em geral',
      'Indenizações',
      'Obrigações e cobranças',
    ],
  },
  {
    icon: 'ri-building-2-line',
    titulo: 'Imobiliário',
    itens: [
      'Compra, venda e regularização',
      'Ações possessórias',
      'Distrato e atraso na entrega',
      'Usucapião',
    ],
  },
  {
    icon: 'ri-shield-user-line',
    titulo: 'Consumidor',
    itens: [
      'Cobranças e negativações indevidas',
      'Fraudes bancárias',
      'Empréstimos consignados',
      'Direito à saúde',
    ],
  },
  {
    icon: 'ri-heart-line',
    titulo: 'Família',
    itens: [
      'Divórcio e união estável',
      'Guarda e regulamentação de visitas',
      'Partilha de bens',
      'Alienação parental',
    ],
  },
  {
    icon: 'ri-file-list-3-line',
    titulo: 'Sucessões',
    itens: [
      'Inventário judicial e extrajudicial',
      'Testamentos',
      'Partilha e sobrepartilha',
      'Interdição e curatela',
    ],
  },
  {
    icon: 'ri-briefcase-4-line',
    titulo: 'Trabalhista',
    itens: [
      'Contencioso trabalhista',
      'Consultoria e pareceres',
      'Assessoria a RH e Pessoal',
      'Defesa em reclamações',
    ],
  },
  {
    icon: 'ri-store-2-line',
    titulo: 'Empresarial',
    itens: [
      'Constituição e dissolução',
      'Contratos empresariais',
      'Recuperação de crédito',
      'Assessoria preventiva',
    ],
  },
  {
    icon: 'ri-flashlight-line',
    titulo: 'Energia',
    itens: [
      'Assessoria a concessionárias',
      'Contratos de transmissão e geração',
      'Regulação setorial (ANEEL)',
      'Contencioso regulatório',
    ],
  },
];

const AreaCard = ({ area }: { area: Area }) => (
  <div className="group relative bg-[#1E3A5F]/40 border border-white/5 p-4 hover:border-[#C8A96A]/40 transition-all duration-400 cursor-pointer flex flex-col">
    <div className="w-6 h-0.5 bg-[#C8A96A] mb-3 group-hover:w-10 transition-all duration-300" />
    <div className="w-8 h-8 flex items-center justify-center mb-3">
      <i className={`${area.icon} text-[#C8A96A] text-xl`}></i>
    </div>
    <h3 className="text-[#F9FAFB] text-xs font-bold mb-3 tracking-widest uppercase">{area.titulo}</h3>
    <ul className="flex flex-col gap-1.5 flex-grow mb-4">
      {area.itens.map((item, i) => (
        <li key={i} className="flex items-start gap-1.5 text-[#E5E7EB]/60 text-[11px] leading-snug">
          <span className="text-[#C8A96A] mt-0.5 flex-shrink-0">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
    <a
      href="#contato"
      className="flex items-center gap-1.5 text-[#C8A96A] text-[10px] font-medium tracking-widest uppercase group-hover:gap-3 transition-all duration-300 cursor-pointer mt-auto"
    >
      <span>Consultar</span>
      <i className="ri-arrow-right-line text-xs"></i>
    </a>
  </div>
);

const AreasSection = () => {
  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent((c) => Math.max(c - 1, 0));
  const next = () => setCurrent((c) => Math.min(c + 1, areas.length - 1));

  return (
    <section id="areas" className="py-12 md:py-20 bg-[#0F1E2E]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-10 bg-[#C8A96A]" />
            <span className="text-[#C8A96A] text-xs font-semibold tracking-[0.35em] uppercase">
              Expertise Jurídica
            </span>
            <div className="h-px w-10 bg-[#C8A96A]" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#F9FAFB] mb-3">
            Áreas de Atuação
          </h2>
          <p className="text-[#E5E7EB]/60 text-sm max-w-xl mx-auto font-light">
            Atuação integrada e estratégica nas principais áreas do direito, com soluções
            personalizadas para cada demanda.
          </p>
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden">
          <div className="relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-300 ease-in-out"
                style={{ transform: `translateX(-${current * 100}%)` }}
              >
                {areas.map((area) => (
                  <div key={area.titulo} className="w-full flex-shrink-0 px-1">
                    <AreaCard area={area} />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between mt-6">
              <button
                onClick={prev}
                disabled={current === 0}
                className="w-10 h-10 flex items-center justify-center border border-white/20 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
                aria-label="Anterior"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>

              <div className="flex items-center gap-2">
                {areas.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrent(i)}
                    className={`h-1.5 rounded-full transition-all duration-200 cursor-pointer ${
                      i === current ? 'bg-[#C8A96A] w-4' : 'bg-white/20 w-1.5'
                    }`}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={next}
                disabled={current === areas.length - 1}
                className="w-10 h-10 flex items-center justify-center border border-white/20 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
                aria-label="Próximo"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Grid - 8 colunas */}
        <div className="hidden md:grid grid-cols-4 lg:grid-cols-8 gap-px bg-white/5">
          {areas.map((area) => (
            <AreaCard key={area.titulo} area={area} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-8 md:mt-10">
          <a
            href="#contato"
            className="inline-block px-10 md:px-12 py-4 border border-[#C8A96A] text-[#C8A96A] text-xs font-semibold tracking-[0.25em] uppercase hover:bg-[#C8A96A] hover:text-[#0F1E2E] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Agende uma consulta
          </a>
        </div>
      </div>
    </section>
  );
};

export default AreasSection;
